import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { resolveImageUrl } from '../services/apiService';
import { KitchenRowCard, LoadingSpinner, EmptyState } from '../components';
import { useLocation as useLocationCtx, createRestaurantSlug } from '../context/LocationContext';

const US_STATES = {
  AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California',
  CO:'Colorado', CT:'Connecticut', DE:'Delaware', FL:'Florida', GA:'Georgia',
  HI:'Hawaii', ID:'Idaho', IL:'Illinois', IN:'Indiana', IA:'Iowa',
  KS:'Kansas', KY:'Kentucky', LA:'Louisiana', ME:'Maine', MD:'Maryland',
  MA:'Massachusetts', MI:'Michigan', MN:'Minnesota', MS:'Mississippi', MO:'Missouri',
  MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire', NJ:'New Jersey',
  NM:'New Mexico', NY:'New York', NC:'North Carolina', ND:'North Dakota', OH:'Ohio',
  OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania', RI:'Rhode Island', SC:'South Carolina',
  SD:'South Dakota', TN:'Tennessee', TX:'Texas', UT:'Utah', VT:'Vermont',
  VA:'Virginia', WA:'Washington', WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming',
  DC:'Washington DC',
};
const expandState = (s) => { if (!s) return ''; const u = s.trim().toUpperCase(); return US_STATES[u] || s; };

const SearchPage = () => {
  const navigate = useNavigate();
  const { locationSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedLocation } = useLocationCtx();

  const query = searchParams.get('q') || '';

  const [kitchenResults, setKitchenResults] = useState([]);
  const [dishResults, setDishResults]       = useState([]);
  const [isLoading, setIsLoading]           = useState(false);

  // Build location-aware kitchen URL
  const getKitchenPath = (kitchen) => {
    const kid  = kitchen.kitchen_id || kitchen.id;
    const slug = locationSlug || selectedLocation?.slug;
    if (slug) {
      const kSlug = createRestaurantSlug(kitchen.kitchen_name || kitchen.name) || kid;
      return `/customer/${slug}/${kSlug}`;
    }
    return `/customer/kitchen/${kid}`;
  };

  // Unified search: kitchens + dishes (also matches by location/address)
  const runSearch = useCallback(async (q) => {
    if (!q.trim()) return;
    setIsLoading(true);
    try {
      const lower = q.toLowerCase();

      // ── Kitchens ──────────────────────────────────────────────────────────
      // Pass search to backend (handles state name → abbreviation mapping)
      // Also do client-side filtering as a safety net
      const kitchensRes = await apiService.getKitchens({ limit: 200, search: q });
      const allKitchens = kitchensRes?.data?.kitchens || kitchensRes?.data || [];

      const matchedKitchens = allKitchens.filter(k => {
        const fields = [
          k.kitchen_name, k.name, k.cuisine_type,
          k.address_street, k.address_city, k.address_zip,
          k.address_state,                // raw abbreviation e.g. "FL"
          expandState(k.address_state),   // full name e.g. "Florida"
        ];
        return fields.some(f => f && f.toLowerCase().includes(lower));
      });
      setKitchenResults(matchedKitchens);

      // ── Dishes ────────────────────────────────────────────────────────────
      const allDishes = [];
      for (const kitchen of allKitchens.slice(0, 30)) {
        const kid = kitchen.kitchen_id || kitchen.id;
        try {
          const menuRes = await apiService.getKitchenMenu(kid);
          const raw = menuRes?.data || [];
          let flat = [];
          if (Array.isArray(raw)) {
            if (raw.length > 0 && raw[0]?.items) {
              raw.forEach(group =>
                (group.items || []).forEach(item =>
                  flat.push({ ...item, _kitchenId: kid, _kitchenName: kitchen.kitchen_name || kitchen.name, _kitchen: kitchen })
                )
              );
            } else {
              flat = raw.map(item => ({ ...item, _kitchenId: kid, _kitchenName: kitchen.kitchen_name || kitchen.name, _kitchen: kitchen }));
            }
          }
          const matched = flat.filter(item =>
            [item.item_name, item.name, item.description, item.category]
              .some(f => f && f.toLowerCase().includes(lower))
          );
          allDishes.push(...matched);
          if (allDishes.length >= 24) break;
        } catch { /* skip kitchen on error */ }
      }
      setDishResults(allDishes.slice(0, 24));
    } catch (e) {
      console.error('Search error', e);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (query) runSearch(query);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const hasResults = kitchenResults.length > 0 || dishResults.length > 0;

  // ── Dish card ────────────────────────────────────────────────────────────
  const DishCard = ({ dish }) => {
    const [imgErr, setImgErr] = React.useState(false);
    const isVeg = dish.is_veg !== false && dish.food_type !== 'non_veg';
    const imgSrc = (!imgErr && dish.image_url) ? resolveImageUrl(dish.image_url)
      : 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=300&h=200&fit=crop';

    return (
      <div
        className="srp-dish-card"
        onClick={() => navigate(getKitchenPath(dish._kitchen || { id: dish._kitchenId, kitchen_name: dish._kitchenName }))}
      >
        <div className="srp-dish-img-wrap">
          <img src={imgSrc} alt={dish.item_name || dish.name} className="srp-dish-img" onError={() => setImgErr(true)} />
          <div className={`srp-veg-badge ${isVeg ? 'veg' : 'nonveg'}`}>
            <div className="srp-veg-dot" />
          </div>
        </div>
        <div className="srp-dish-body">
          <div className="srp-dish-name">{dish.item_name || dish.name}</div>
          <div className="srp-dish-kitchen">{dish._kitchenName}</div>
          <div className="srp-dish-price">${parseFloat(dish.price || 0).toFixed(2)}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="srp-page">
      <div className="srp-heading">
        {query
          ? <><span className="srp-heading-label">Results for </span><span className="srp-heading-query">"{query}"</span></>
          : <span className="srp-heading-label">Search results</span>
        }
        {!isLoading && hasResults && (
          <span className="srp-heading-sub">
            {kitchenResults.length > 0 && dishResults.length > 0
              ? `${kitchenResults.length} kitchen${kitchenResults.length !== 1 ? 's' : ''} · ${dishResults.length} dish${dishResults.length !== 1 ? 'es' : ''}`
              : kitchenResults.length > 0
              ? `${kitchenResults.length} kitchen${kitchenResults.length !== 1 ? 's' : ''}`
              : `${dishResults.length} dish${dishResults.length !== 1 ? 'es' : ''}`
            }
          </span>
        )}
      </div>

      {isLoading ? (
        <LoadingSpinner text="Searching…" />
      ) : !query ? (
        <EmptyState icon="🔍" title="Enter something to search" subtitle="Use the search bar above" />
      ) : !hasResults ? (
        <EmptyState icon="🔍" title="No results found" subtitle={`No kitchens or dishes match "${query}"`} />
      ) : (
        <div className="srp-body">

          {/* ── Kitchens ─────────────────────────────────────────────────── */}
          {kitchenResults.length > 0 && (
            <section className="srp-section">
              <h2 className="srp-section-title">
                Kitchens <span className="srp-section-count">{kitchenResults.length}</span>
              </h2>
              <div className="srp-kitchen-list">
                {kitchenResults.map((k, idx) => (
                  <KitchenRowCard
                    key={k.kitchen_id || k.id}
                    kitchen={k}
                    index={idx}
                    onClick={() => navigate(getKitchenPath(k))}
                    selectedLocation={selectedLocation}
                  />
                ))}
              </div>
            </section>
          )}

          {/* ── Dishes ───────────────────────────────────────────────────── */}
          {dishResults.length > 0 && (
            <section className="srp-section">
              <h2 className="srp-section-title">
                Dishes <span className="srp-section-count">{dishResults.length}</span>
              </h2>
              <div className="srp-dish-grid">
                {dishResults.map((dish, idx) => (
                  <DishCard key={`${dish._kitchenId}-${dish.item_id || dish.id || idx}`} dish={dish} />
                ))}
              </div>
            </section>
          )}

        </div>
      )}
    </div>
  );
};

export default SearchPage;
